import { parsePartialJSON } from '@tanstack/ai'
import { mentionsToNames } from '../../../shared/mentions.js'
import { WIDGET_SCHEMAS } from '../../../shared/widgets.js'
import { widgetText } from './widgets/text.js'

// Turns an assistant UIMessage into the pieces the answer view renders:
//   meta     — set_answer_meta input (mode, pipe)
//   blocks   — prose and widgets, in the order the model produced them. A
//              finished widget whose input fails its schema (an old transcript,
//              bad model output) is dropped; legacy tool names never render.
//   sources  — the last cite_sources call (and the server's resolved links)
//   followups — suggested next questions (suggest_followups)
//   activity — retrieval steps, shown only while the answer streams
//   writing  — the newest part is prose (the status line says "Writing the answer")

// Section references like "(see §3.4)" or "§6.4" are dropped from prose.
const SECTION_REF = /\s*\((?:see\s+)?§\s?\d+(?:\.\d+)*[^)]*\)|\s?(?:see\s+)?§\s?\d+(?:\.\d+)*/gi

export function stripSectionRefs(text) {
  return text.replace(SECTION_REF, '').replace(/§/g, '')
}

const COMPLETE_STATES = new Set(['input-complete', 'approval-responded', 'complete'])

function parseResult(content) {
  if (content === undefined || content === null) return null
  if (typeof content === 'object') return content
  try {
    return JSON.parse(content)
  } catch {
    return { raw: content }
  }
}

function toolInput(part) {
  if (part.input && typeof part.input === 'object') return part.input
  if (!part.arguments) return {}
  try {
    return JSON.parse(part.arguments)
  } catch {
    return parsePartialJSON(part.arguments) ?? {}
  }
}

export function buildAnswer(message) {
  const parts = message?.parts ?? []
  const results = new Map()
  for (const part of parts) {
    if (part.type === 'tool-result') results.set(part.toolCallId, parseResult(part.content))
  }

  let meta = null
  let sources = null
  let followups = []
  const blocks = []
  const activity = []

  parts.forEach((part, index) => {
    if (part.type === 'text') {
      const text = stripSectionRefs(part.content ?? '')
      if (text.trim()) blocks.push({ kind: 'text', key: `text-${index}`, text })
      return
    }
    if (part.type !== 'tool-call') return
    const input = toolInput(part)
    const result = results.get(part.id) ?? parseResult(part.output)
    const complete = COMPLETE_STATES.has(part.state) || Boolean(result)
    switch (part.name) {
      case 'set_answer_meta':
        meta = { ...meta, ...input }
        break
      case 'suggest_followups':
        // Shown and asked in plain words: system names, never @ tags.
        followups = Array.isArray(input.questions)
          ? input.questions.filter((q) => typeof q === 'string' && q.trim()).map(mentionsToNames)
          : []
        break
      case 'cite_sources':
        sources = { input, result, complete }
        break
      case 'read_skill_file':
      case 'search_skill':
        activity.push({ key: part.id, name: part.name, input, done: Boolean(result) })
        break
      default: {
        const schema = WIDGET_SCHEMAS[part.name]
        if (!schema) break
        let value = input
        if (complete) {
          const parsed = schema.safeParse(input)
          if (!parsed.success) break
          value = parsed.data
        }
        blocks.push({ kind: part.name, key: part.id, input: value, result, complete, error: part.state === 'error' })
      }
    }
  })

  // The model is writing prose right now (last part is text), for the status line.
  const lastPart = parts.findLast((p) => p.type === 'text' || p.type === 'tool-call')
  const writing = lastPart?.type === 'text'
  return { meta, blocks: pairEndpoints(attachGotchas(blocks)), sources, activity, followups, writing }
}

const norm = (value) => String(value ?? '').trim().toLowerCase()

/** "order.create_tx" matches "order.create_tx" and "order.create_tx · Transaction API". */
export function refMatches(ref, candidate) {
  const a = norm(ref)
  const b = norm(candidate)
  return Boolean(a) && (a === b || (b.startsWith(a) && /^[^\w.]/.test(b.slice(a.length))))
}

function hostRefs(block) {
  const input = block.input ?? {}
  switch (block.kind) {
    case 'show_endpoint':
      return { kind: 'endpoint', refs: [input.op_id] }
    case 'show_fields':
      return { kind: 'field', refs: (input.fields ?? []).flatMap((f) => [f?.name, f?.target]) }
    case 'show_pipe_flow':
      return { kind: 'step', refs: (input.flows ?? []).flatMap((f) => (f?.steps ?? []).map((s) => s?.op_id)) }
    case 'show_handoff':
      return { kind: 'step', refs: (input.steps ?? []).map((s) => s?.op_id) }
    default:
      return null
  }
}

/**
 * Gotchas with attach_to move onto the card they belong to (an endpoint by
 * op_id, a mapping row by field, a flow or handoff step by op_id). Unmatched
 * ones stay in the list; a list left empty is dropped once it is complete.
 */
function attachGotchas(blocks) {
  const hosts = blocks.map((block) => ({ block, host: hostRefs(block) })).filter((h) => h.host)
  const out = []
  for (const block of blocks) {
    if (block.kind !== 'show_gotchas') {
      out.push(block)
      continue
    }
    const rest = []
    for (const item of block.input?.items ?? []) {
      const target = item?.attach_to
        ? hosts.find(({ host }) => host.kind === item.attach_to.kind && host.refs.some((r) => refMatches(item.attach_to.ref, r)))
        : null
      if (target) target.block.gotchas = [...(target.block.gotchas ?? []), item]
      else rest.push(item)
    }
    if (rest.length || !block.complete) out.push({ ...block, input: { ...block.input, items: rest } })
  }
  return out
}

/**
 * When one answer has both a REST endpoint and an OData query, they share one
 * card with an "Entity API / OData query" switch.
 */
function pairEndpoints(blocks) {
  const isRest = (b) => b.kind === 'show_endpoint' && b.input?.api !== 'odata' && b.input?.variant !== 'secondary'
  const isOData = (b) => b.kind === 'show_endpoint' && b.input?.api === 'odata'
  const rest = blocks.find(isRest)
  const odata = blocks.find(isOData)
  if (!rest || !odata) return blocks
  rest.alternate = odata
  return blocks.filter((b) => b !== odata)
}

/** Plain-text export of an answer for "Copy answer". `states` holds saved widget edits. */
export function answerToText(answer, states = {}) {
  const out = []
  for (const block of answer.blocks) {
    out.push(block.kind === 'text' ? block.text.trim() : widgetText(block, states?.[block.key]))
  }
  if (answer.sources?.input?.sources?.length) {
    out.push(`Sources: ${answer.sources.input.sources.map((s) => `${s.label}${s.file ? ` (${s.file})` : ''}`).join('; ')}`)
  }
  return out.filter(Boolean).join('\n\n')
}
